/**
 * Speech-synthesis wrapper around `window.speechSynthesis`.
 *
 * Degrades silently everywhere: no Hindi voice, no `speechSynthesis` at all
 * (jsdom in tests, or a browser without support), or a mid-utterance error —
 * `speak` never rejects, it just resolves once the attempt is over. This
 * keeps exercise components free of try/catch around every `speak()` call.
 */

/** The audio surface every exercise component depends on. */
export interface AudioService {
  speak(text: string, opts?: { rate?: number }): Promise<void>;
  hasHindiVoice(): boolean;
  listHindiVoices(): { name: string; lang: string }[];
}

const HINDI_LANG_PREFIX = "hi";

function isHindiVoice(voice: SpeechSynthesisVoice): boolean {
  return voice.lang.toLowerCase().startsWith(HINDI_LANG_PREFIX);
}

/**
 * Standalone one-shot read of the available Hindi voices — for UI like
 * the Settings voice picker that just needs "what's on this device right
 * now" rather than the caching/`voiceschanged`-subscribed list an
 * in-session {@link AudioService} keeps. Returns `[]` when
 * `speechSynthesis` isn't available (SSR, jsdom, unsupported browser).
 */
export function listHindiVoices(): { name: string; lang: string }[] {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  return window.speechSynthesis
    .getVoices()
    .filter(isHindiVoice)
    .map((voice) => ({ name: voice.name, lang: voice.lang }));
}

/**
 * Create an {@link AudioService} backed by the Web Speech API.
 *
 * `getSettings` is called on every `speak()` so the caller's live settings
 * (voice choice, rate) are always honored without this module needing to
 * subscribe to anything.
 */
export function createSpeechAudio(
  getSettings: () => { voiceName: string | null; rate: number },
): AudioService {
  const synth: SpeechSynthesis | undefined =
    typeof window === "undefined" ? undefined : window.speechSynthesis;

  let cachedVoices: SpeechSynthesisVoice[] = synth?.getVoices() ?? [];
  let listenerAttached = false;

  function refreshVoices(): void {
    if (synth === undefined) return;
    cachedVoices = synth.getVoices();
  }

  // Chrome (and some other browsers) load voices asynchronously: an initial
  // getVoices() call can return an empty list, filled in later via the
  // "voiceschanged" event. Listen once, then just re-read the list each time.
  function ensureVoiceListener(): void {
    if (synth === undefined || listenerAttached) return;
    listenerAttached = true;
    synth.addEventListener("voiceschanged", refreshVoices);
  }

  function hindiVoices(): SpeechSynthesisVoice[] {
    ensureVoiceListener();
    if (cachedVoices.length === 0) refreshVoices();
    return cachedVoices.filter(isHindiVoice);
  }

  function hasHindiVoice(): boolean {
    if (synth === undefined) return false;
    return hindiVoices().length > 0;
  }

  function listHindiVoices(): { name: string; lang: string }[] {
    if (synth === undefined) return [];
    return hindiVoices().map((voice) => ({
      name: voice.name,
      lang: voice.lang,
    }));
  }

  function speak(text: string, opts?: { rate?: number }): Promise<void> {
    if (synth === undefined) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      const settings = getSettings();
      utterance.rate = opts?.rate ?? settings.rate;

      const voices = hindiVoices();
      const preferred =
        (settings.voiceName !== null &&
          voices.find((voice) => voice.name === settings.voiceName)) ||
        voices[0];
      if (preferred !== undefined) {
        utterance.voice = preferred;
        utterance.lang = preferred.lang;
      } else {
        utterance.lang = "hi-IN";
      }

      // Never reject: a synthesis error should just end the attempt quietly.
      utterance.addEventListener("end", () => resolve());
      utterance.addEventListener("error", () => resolve());

      synth.speak(utterance);
    });
  }

  return { speak, hasHindiVoice, listHindiVoices };
}
